























































































































import { Vue, Component } from 'vue-property-decorator';

import SignUpService from '@/api/SingupService';
import APIService from '@/api';
import ManagerService from '@/api/ManagerService';
import { parseToBoolean } from '@/utils/parseToBoolean';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import InviteMailBox from '@/componentsV2/molecules/InviteMailBox/InviteMailBox.vue';
import ProviderButton from '@/components/molecules/ProviderButton/ProviderButton.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import SignInService from '@/api/SigninService';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';
import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import HTTPStatus from '@/constants/httpStatus';
import refreshBusinessInformation from '@/utils/refresh-business-information';
import { REDIRECT_LINK_STORAGE } from '@/router/middlewares/HandleSigninRedirectRestrictedPages';
import { SOCIAL_LOGIN_REDIRECT_KEY } from '@/api/SigninService';

interface IUserInfo {
  hasApple: boolean;
  hasGoogle: boolean;
  hasPassword: boolean;
}

@Component({
  components: {
    FilledButton,
    InviteMailBox,
    ProviderButton,
    Input,
    ValidationObserver,
    ValidationProvider
  }
})
export default class InvitationLoginAccountOwner extends Vue {
  signUpService = new SignUpService({
    disableToastMessage: true
  });
  managerService = new ManagerService();
  service: SignInService = new SignInService();

  loading = false;
  inviteData: Record<string, any> = {};

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  form = {
    password: ''
  };

  hasSocialLogin: boolean = false;

  userInfo: IUserInfo | null = null;

  created() {
    const query = this.$route.query;

    const queryParams = Object.assign({}, this.$route.query, {
      isManagerRegistered: parseToBoolean(query.isManagerRegistered),
      isInvitedManager: parseToBoolean(query.isInvitedManager)
    });

    this.inviteData = queryParams;

    // verifyAccountExists throws when the invited e-mail lookup returns no
    // data; without this catch the rejection is silent and the screen stays
    // blank (userInfo null) with no way out.
    this.verifyAccountExists().catch((err: any) => {
      this.$notification(
        (err && err.message) || 'Something went wrong. Please try again.',
        'error'
      );
    });
  }

  async verifyAccountExists() {
    const response = await this.signUpService.verifyIfEmailExists(
      this.inviteData.invitedMail
    );

    const { data } = response;

    if (!data) {
      const messageError = this.getMessageErrorLogion(response);
      throw new Error(messageError);
    }

    this.hasSocialLogin =
      (data.hasApple || data.hasGoogle) && !data.hasPassword;

    this.userInfo = data;
  }

  goToForgot() {
    return `/n?goBack=true&inviteCode=${this.inviteData.inviteCode}`;
  }

  async acceptInvite() {
    const { data } = await this.managerService.acceptInvite(
      this.inviteData.inviteCode
    );

    this.service.setCredentials(data.access_token, false);

    // Clear stale super-admin / redirect keys before the handoff so neither
    // the /home path nor the /login?token= fallback carries them into the
    // next navigation.
    localStorage.removeItem(
      `${window.location.host}-${APIService.SUPER_ADMIN}`
    );
    localStorage.removeItem(REDIRECT_LINK_STORAGE);
    localStorage.removeItem(SOCIAL_LOGIN_REDIRECT_KEY);

    // Exchange the fresh (business-aware) accept token for the session cookie
    // BEFORE any further request or navigation. Until the exchange lands, the
    // cookie is still the pre-accept one (no business): business-scoped calls
    // made with it — refreshBusinessInformation here, or the /login?token=
    // page boot racing the exchange — 403 and pop LostAccessModal on the
    // invited owner (TWISM-18461). Reloading '/' directly is equally wrong:
    // it reuses the pre-accept cookie and strands the owner on /manager.
    try {
      await this.service.exchangeUrlTokenForCookie(data.access_token);
    } catch {
      // Exchange failed here — retry it via the login-with-token route.
      window.location.href = `/login?token=${data.access_token}`;
      return;
    }

    await refreshBusinessInformation();

    window.location.href = '/home';

    this.loading = false;
  }

  async loginAndClaimAccount() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');
      return;
    }

    try {
      this.loading = true;

      const payloadLogin = {
        email: this.inviteData.invitedMail,
        password: this.form.password,
        authType: 'manager'
      };

      const { data, ...rest } = (await this.service.login(
        payloadLogin.password,
        payloadLogin.email.trim(),
        payloadLogin.authType
      )) as any;

      if (!data) {
        const messageError = this.getMessageErrorLogion(rest.response);
        throw new Error(messageError);
      }

      this.service.setCredentials(data.access_token, false);

      await this.acceptInvite();

      this.loading = false;
    } catch (err) {
      this.$notification((err as any).message, 'error');
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }

  getMessageErrorLogion(response: any): string {
    const statusCode = response.status;
    const data = response.data;

    if (statusCode === HTTPStatus.TOO_MANY_REQUESTS) {
      return data.message[0].constraints.TOO_MANY_REQUESTS;
    }

    return data.message[0].translation.title;
  }

  providerSignin(name: 'google' | 'apple') {
    this.loading = true;

    this.service.loginWithProvider(name, {
      ...{
        email: this.inviteData.invitedMail,
        inviteCode: this.inviteData.inviteCode,
        redirect: '/'
      }
    });
  }
}
